import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { z } from "zod";
import { apagar, editarJson, enviarFormulario, enviarJson, pedirJson } from "./api";

/* O cadastro que a equipe mantém sobre clientes e pessoas: organização ampliada (classificação,
   campos adicionais), contatos, endereços, importação CSV e fusão de duplicadas. O contrato é o
   de `api/app/dominios/cadastro/` e `organizacoes/`. */

export const CHAVE_DO_CADASTRO = ["cadastro"] as const;

const uuid = z.string().uuid();

export const CLASSIFICACOES = ["estrategico", "importante", "padrao"] as const;
export type Classificacao = (typeof CLASSIFICACOES)[number];

export const ROTULO_DA_CLASSIFICACAO: Record<Classificacao, string> = {
  estrategico: "Estratégico",
  importante: "Importante",
  padrao: "Padrão",
};

const esquemaClassificacao = z.enum(CLASSIFICACOES);

export const esquemaOrganizacaoDoCadastro = z.object({
  id: uuid,
  cnpj: z.string(),
  cnpj_formatado: z.string(),
  razao_social: z.string(),
  nome_fantasia: z.string().nullable(),
  cidade: z.string().nullable(),
  uf: z.string().nullable(),
  segmento: z.string().nullable(),
  classificacao: esquemaClassificacao.nullable().optional(),
  campos_adicionais: z.record(z.string(), z.string()).optional(),
  criada_em: z.string(),
});

const esquemaContato = z.object({
  id: uuid,
  organizacao_id: uuid,
  nome: z.string(),
  cargo: z.string().nullable(),
  email: z.string().nullable(),
  telefone: z.string().nullable(),
  observacao: z.string().nullable(),
  principal: z.boolean(),
  criado_em: z.string(),
});

const esquemaEndereco = z.object({
  id: uuid,
  organizacao_id: uuid,
  rotulo: z.string(),
  cep: z.string().nullable(),
  logradouro: z.string().nullable(),
  numero: z.string().nullable(),
  complemento: z.string().nullable(),
  bairro: z.string().nullable(),
  cidade: z.string().nullable(),
  uf: z.string().nullable(),
  principal: z.boolean(),
  criado_em: z.string(),
});

export const esquemaPessoaDoCadastro = z.object({
  id: uuid,
  email: z.string(),
  nome: z.string(),
  cargo: z.string().nullable(),
  telefone: z.string().nullable(),
  superior_id: uuid.nullable().optional(),
  superior_nome: z.string().nullable().optional(),
  organizacao_id: uuid.nullable(),
  interno: z.boolean(),
  ativo: z.boolean(),
  acesso_definido: z.boolean(),
  papeis: z.array(z.string()),
});

const esquemaLinhaDaImportacao = z.object({
  linha: z.number().int(),
  acao: z.enum(["criar", "atualizar", "sem_mudanca", "erro"]),
  chave: z.string().nullable(),
  rotulo: z.string().nullable(),
  erros: z.array(z.string()),
});

const esquemaRelatorioDaImportacao = z.object({
  tipo: z.enum(["organizacoes", "pessoas"]),
  aplicada: z.boolean(),
  total: z.number().int(),
  criar: z.number().int(),
  atualizar: z.number().int(),
  sem_mudanca: z.number().int(),
  com_erro: z.number().int(),
  aplicadas: z.number().int(),
  colunas_reconhecidas: z.array(z.string()),
  colunas_ignoradas: z.array(z.string()),
  linhas: z.array(esquemaLinhaDaImportacao),
  erros: z.array(
    z.object({ linha: z.number().int(), campo: z.string().nullable(), mensagem: z.string() }),
  ),
});

const esquemaGrupoDeDuplicados = z.object({
  motivo: z.string(),
  itens: z.array(z.record(z.string(), z.string().nullable())),
});

const esquemaPreviaDaFusao = z.object({
  destino_id: uuid,
  destino_nome: z.string(),
  origem_id: uuid,
  origem_nome: z.string(),
  itens: z.array(
    z.object({ tabela: z.string(), rotulo: z.string(), quantidade: z.number().int() }),
  ),
  total: z.number().int(),
  avisos: z.array(z.string()),
  // A trava: o POST da fusão só vale com este token (do par e de quem pediu a prévia) e, quando a
  // prévia pede, com o nome da absorvida digitado.
  token_de_confirmacao: z.string(),
  expira_em: z.string().nullable(),
  exige_confirmacao_digitada: z.boolean(),
});

const esquemaResultadoDaFusao = z.object({
  destino_id: uuid,
  origem_id: uuid,
  movidos: z.record(z.string(), z.number().int()),
  total: z.number().int(),
  ajustes: z.array(z.string()),
});

export type OrganizacaoDoCadastro = z.infer<typeof esquemaOrganizacaoDoCadastro>;
export type ContatoDaOrganizacao = z.infer<typeof esquemaContato>;
export type EnderecoDaOrganizacao = z.infer<typeof esquemaEndereco>;
export type PessoaDoCadastro = z.infer<typeof esquemaPessoaDoCadastro>;
export type RelatorioDaImportacao = z.infer<typeof esquemaRelatorioDaImportacao>;
export type GrupoDeDuplicados = z.infer<typeof esquemaGrupoDeDuplicados>;
export type PreviaDaFusao = z.infer<typeof esquemaPreviaDaFusao>;
export type ResultadoDaFusao = z.infer<typeof esquemaResultadoDaFusao>;

/* ------------------------------------------------------------------------- helpers puros */

/** A ficha de uma organização no console. Um lugar só, para as outras telas apontarem. */
export function caminhoDaOrganizacao(id: string): string {
  return `/admin/cadastros/organizacoes/${encodeURIComponent(id)}`;
}

export function nomeDaOrganizacao(
  organizacao: Pick<OrganizacaoDoCadastro, "razao_social" | "nome_fantasia">,
): string {
  return organizacao.nome_fantasia?.trim() || organizacao.razao_social;
}

/** Só dígitos do CEP, com hífen para exibir: `89251000` -> `89251-000`. */
export function cepLegivel(cep: string | null): string {
  if (!cep) return "";
  const digitos = cep.replace(/\D/g, "");
  return digitos.length === 8 ? `${digitos.slice(0, 5)}-${digitos.slice(5)}` : cep;
}

/** Linhas do editor de campos adicionais -> o objeto que a API espera (ignora linhas vazias). */
export function camposParaCorpo(linhas: ReadonlyArray<{ nome: string; valor: string }>) {
  const corpo: Record<string, string> = {};
  for (const linha of linhas) {
    const nome = linha.nome.trim();
    if (nome) corpo[nome] = linha.valor.trim();
  }
  return corpo;
}

export function linhasDeCampos(campos: Record<string, string> | undefined) {
  return Object.entries(campos ?? {}).map(([nome, valor]) => ({ nome, valor }));
}

export const LIMITE_DE_CAMPOS_ADICIONAIS = 20;

/** Confere o editor antes de enviar; devolve a frase do problema ou `null`. */
export function validarCampos(linhas: ReadonlyArray<{ nome: string; valor: string }>): string | null {
  const nomes = linhas.map((linha) => linha.nome.trim()).filter(Boolean);
  if (nomes.length > LIMITE_DE_CAMPOS_ADICIONAIS) {
    return `Use no máximo ${LIMITE_DE_CAMPOS_ADICIONAIS} campos adicionais.`;
  }
  const vistos = new Set<string>();
  for (const nome of nomes) {
    const chave = nome.toLocaleLowerCase("pt-BR");
    if (vistos.has(chave)) return `O campo “${nome}” aparece duas vezes.`;
    vistos.add(chave);
    if (nome.length > 60) return "O nome de um campo adicional tem no máximo 60 caracteres.";
  }
  if (linhas.some((linha) => linha.valor.trim().length > 300)) {
    return "O valor de um campo adicional tem no máximo 300 caracteres.";
  }
  return null;
}

export const ROTULO_DA_ACAO_DA_IMPORTACAO: Record<string, string> = {
  criar: "Criar",
  atualizar: "Atualizar",
  sem_mudanca: "Sem mudança",
  erro: "Erro",
};

/** O CSV dos erros da importação, para a pessoa corrigir a planilha (aspas dobradas, `;`). */
export function montarCsvDeErros(relatorio: Pick<RelatorioDaImportacao, "erros">): string {
  const escapar = (valor: string) => `"${valor.replaceAll('"', '""')}"`;
  const linhas = relatorio.erros.map((erro) =>
    [String(erro.linha), escapar(erro.campo ?? ""), escapar(erro.mensagem)].join(";"),
  );
  return ["linha;campo;mensagem", ...linhas].join("\r\n");
}

/** Modelo de planilha que a tela oferece para baixar. */
export const MODELO_DE_ORGANIZACOES =
  "cnpj;razao_social;nome_fantasia;cidade;uf;segmento;classificacao\r\n";
export const MODELO_DE_PESSOAS = "email;nome;cargo;telefone;cnpj;papel;superior_email\r\n";

/* ----------------------------------------------------------------------------- consultas */

function invalidar(cliente: ReturnType<typeof useQueryClient>) {
  void cliente.invalidateQueries({ queryKey: CHAVE_DO_CADASTRO });
}

export function useOrganizacoesDoCadastro(
  filtros: { busca?: string; classificacao?: string } = {},
  habilitado = true,
) {
  const parametros = new URLSearchParams({ limite: "200" });
  if (filtros.busca?.trim()) parametros.set("busca", filtros.busca.trim());
  if (filtros.classificacao) parametros.set("classificacao", filtros.classificacao);
  return useQuery({
    queryKey: [...CHAVE_DO_CADASTRO, "organizacoes", filtros.busca ?? "", filtros.classificacao ?? ""],
    enabled: habilitado,
    queryFn: ({ signal }) =>
      pedirJson(`/organizacoes?${parametros.toString()}`, z.array(esquemaOrganizacaoDoCadastro), {
        signal,
      }),
  });
}

export function useOrganizacaoDoCadastro(id: string) {
  return useQuery({
    queryKey: [...CHAVE_DO_CADASTRO, "organizacao", id],
    queryFn: ({ signal }) =>
      pedirJson(`/organizacoes/${encodeURIComponent(id)}`, esquemaOrganizacaoDoCadastro, { signal }),
  });
}

export function useAlterarOrganizacao(id: string) {
  const cliente = useQueryClient();
  return useMutation({
    mutationFn: (corpo: Record<string, unknown>) =>
      editarJson(`/organizacoes/${encodeURIComponent(id)}`, esquemaOrganizacaoDoCadastro, corpo),
    onSuccess: () => invalidar(cliente),
  });
}

export function useContatos(organizacaoId: string) {
  return useQuery({
    queryKey: [...CHAVE_DO_CADASTRO, "contatos", organizacaoId],
    queryFn: ({ signal }) =>
      pedirJson(
        `/organizacoes/${encodeURIComponent(organizacaoId)}/contatos`,
        z.array(esquemaContato),
        { signal },
      ),
  });
}

export function useSalvarContato(organizacaoId: string) {
  const cliente = useQueryClient();
  return useMutation({
    mutationFn: ({ id, corpo }: { id?: string; corpo: Record<string, unknown> }) =>
      id
        ? editarJson(
            `/organizacoes/${encodeURIComponent(organizacaoId)}/contatos/${encodeURIComponent(id)}`,
            esquemaContato,
            corpo,
          )
        : enviarJson(
            `/organizacoes/${encodeURIComponent(organizacaoId)}/contatos`,
            esquemaContato,
            corpo,
          ),
    onSuccess: () => invalidar(cliente),
  });
}

export function useRemoverContato(organizacaoId: string) {
  const cliente = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      apagar(
        `/organizacoes/${encodeURIComponent(organizacaoId)}/contatos/${encodeURIComponent(id)}`,
      ),
    onSuccess: () => invalidar(cliente),
  });
}

export function useEnderecos(organizacaoId: string) {
  return useQuery({
    queryKey: [...CHAVE_DO_CADASTRO, "enderecos", organizacaoId],
    queryFn: ({ signal }) =>
      pedirJson(
        `/organizacoes/${encodeURIComponent(organizacaoId)}/enderecos`,
        z.array(esquemaEndereco),
        { signal },
      ),
  });
}

export function useSalvarEndereco(organizacaoId: string) {
  const cliente = useQueryClient();
  return useMutation({
    mutationFn: ({ id, corpo }: { id?: string; corpo: Record<string, unknown> }) =>
      id
        ? editarJson(
            `/organizacoes/${encodeURIComponent(organizacaoId)}/enderecos/${encodeURIComponent(id)}`,
            esquemaEndereco,
            corpo,
          )
        : enviarJson(
            `/organizacoes/${encodeURIComponent(organizacaoId)}/enderecos`,
            esquemaEndereco,
            corpo,
          ),
    onSuccess: () => invalidar(cliente),
  });
}

export function useRemoverEndereco(organizacaoId: string) {
  const cliente = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      apagar(
        `/organizacoes/${encodeURIComponent(organizacaoId)}/enderecos/${encodeURIComponent(id)}`,
      ),
    onSuccess: () => invalidar(cliente),
  });
}

export function usePessoasDaOrganizacao(organizacaoId: string | null) {
  return useQuery({
    queryKey: [...CHAVE_DO_CADASTRO, "pessoas", organizacaoId],
    enabled: organizacaoId !== null,
    queryFn: ({ signal }) =>
      pedirJson(
        `/organizacoes/${encodeURIComponent(organizacaoId ?? "")}/pessoas`,
        z.array(esquemaPessoaDoCadastro),
        { signal },
      ),
  });
}

export function useAlterarPessoa() {
  const cliente = useQueryClient();
  return useMutation({
    mutationFn: ({ id, corpo }: { id: string; corpo: Record<string, unknown> }) =>
      editarJson(`/usuarios/${encodeURIComponent(id)}`, esquemaPessoaDoCadastro, corpo),
    onSuccess: () => {
      invalidar(cliente);
      void cliente.invalidateQueries({ queryKey: ["usuarios"] });
    },
  });
}

/* ------------------------------------------------------------------------------- importação */

export type TipoDeImportacao = "organizacoes" | "pessoas";

export function useImportarPlanilha(tipo: TipoDeImportacao) {
  const cliente = useQueryClient();
  return useMutation({
    mutationFn: ({
      arquivo,
      aplicar,
      atualizarExistentes,
    }: {
      arquivo: File;
      aplicar: boolean;
      atualizarExistentes: boolean;
    }) => {
      const formulario = new FormData();
      formulario.set("arquivo", arquivo);
      formulario.set("atualizar_existentes", atualizarExistentes ? "true" : "false");
      return enviarFormulario(
        `/cadastro/importacoes/${tipo}${aplicar ? "" : "/previa"}`,
        esquemaRelatorioDaImportacao,
        formulario,
      );
    },
    onSuccess: (relatorio) => {
      if (relatorio.aplicada) invalidar(cliente);
    },
  });
}

/* ----------------------------------------------------------------------- duplicados e fusão */

export function useDuplicados(tipo: TipoDeImportacao) {
  return useQuery({
    queryKey: [...CHAVE_DO_CADASTRO, "duplicados", tipo],
    queryFn: ({ signal }) =>
      pedirJson(`/cadastro/duplicados/${tipo}`, z.array(esquemaGrupoDeDuplicados), { signal }),
  });
}

export function useFusao(tipo: TipoDeImportacao) {
  const cliente = useQueryClient();
  const base = tipo === "organizacoes" ? "/organizacoes" : "/usuarios";
  const previa = useMutation({
    mutationFn: ({ destinoId, origemId }: { destinoId: string; origemId: string }) =>
      pedirJson(
        `${base}/${encodeURIComponent(destinoId)}/fusao/previa?origem_id=${encodeURIComponent(origemId)}`,
        esquemaPreviaDaFusao,
      ),
  });
  const fundir = useMutation({
    mutationFn: ({
      destinoId,
      origemId,
      token,
      confirmacaoDigitada,
    }: {
      destinoId: string;
      origemId: string;
      token: string;
      confirmacaoDigitada?: string;
    }) =>
      enviarJson(`${base}/${encodeURIComponent(destinoId)}/fusao`, esquemaResultadoDaFusao, {
        origem_id: origemId,
        token_de_confirmacao: token,
        ...(confirmacaoDigitada !== undefined ? { confirmacao_digitada: confirmacaoDigitada } : {}),
      }),
    onSuccess: () => {
      invalidar(cliente);
      void cliente.invalidateQueries({ queryKey: ["usuarios"] });
      void cliente.invalidateQueries({ queryKey: ["tickets"] });
    },
  });
  return { previa, fundir };
}
